import {
    Network, Cable, Waypoints, RadioTower, ServerCog, ShieldCheck, LockKeyhole,
    HardDrive, Building2, Radar, Video, Cloud, MoveRight, Activity, Wrench,
    Timer, LifeBuoy, Headset, Router,
} from "lucide-react";
import { motion } from "framer-motion";

const SERVICES = [
    { icon: Network, title: "Enterprise Network Design", desc: "Blueprint scalable, secure and redundant networks tailored to your business." },
    { icon: Router, title: "Network Installation", desc: "End-to-end rollout — switches, routers, APs, cabling and configuration." },
    { icon: Cable, title: "Structured Cabling", desc: "Neat, standards-compliant Cat6/6A/7 and fiber cabling with certification." },
    { icon: Waypoints, title: "Fiber Optic Installation", desc: "OS2/OM4 fiber runs, splicing, OTDR testing and distribution boxes." },
    { icon: Building2, title: "Server Rack Installation", desc: "Rack layout, mounting, power distribution and thermal planning." },
    { icon: RadioTower, title: "Wi-Fi Deployment", desc: "Site-surveyed Wi-Fi 6/6E rollouts for offices, campuses and warehouses." },
    { icon: ShieldCheck, title: "Firewall Configuration", desc: "NGFW deployment, policy tuning, IPS and content filtering." },
    { icon: LockKeyhole, title: "VPN Solutions", desc: "Site-to-site and remote-access VPN with zero-trust posture." },
    { icon: HardDrive, title: "NAS Installation", desc: "Enterprise NAS setup, RAID design, replication and backups." },
    { icon: ServerCog, title: "Data Center Setup", desc: "Compute, storage and fabric — designed for uptime and density." },
    { icon: Radar, title: "Network Security", desc: "Segmentation, endpoint hardening, patch and vulnerability management." },
    { icon: Video, title: "CCTV Networking", desc: "PoE switches, VLAN isolation and cabling for surveillance systems." },
    { icon: Cloud, title: "Cloud Networking", desc: "Hybrid cloud connectivity, SD-WAN and secure edge routing." },
    { icon: MoveRight, title: "Network Migration", desc: "Zero-downtime migrations across vendors, sites and technologies." },
    { icon: Activity, title: "Network Monitoring", desc: "24×7 NOC monitoring with alerts, dashboards and SLAs." },
    { icon: Timer, title: "Annual Maintenance (AMC)", desc: "Comprehensive AMC contracts covering hardware, software and labor." },
    { icon: Wrench, title: "Preventive Maintenance", desc: "Scheduled health checks, firmware and cable audits." },
    { icon: LifeBuoy, title: "On-site Technical Support", desc: "Certified field engineers dispatched across pan-India." },
    { icon: Headset, title: "Remote Support", desc: "Rapid remote troubleshooting for switches, firewalls and servers." },
];

export default function Services() {
    return (
        <section id="services" className="py-20 lg:py-28 bg-slate-50" data-testid="services-section">
            <div className="container-lg">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
                    <div>
                        <span className="text-xs font-bold tracking-[0.24em] uppercase text-brand">Services</span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy">
                            Complete networking services,<br />one accountable partner.
                        </h2>
                    </div>
                    <p className="max-w-md text-slate-600">
                        From cabling and configuration to 24×7 support — every service is delivered by
                        certified engineers with structured documentation and SLA-backed response times.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {SERVICES.map((s, i) => (
                        <motion.div
                            key={s.title}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}
                            data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="group rounded-2xl bg-white border border-slate-200 p-6 hover:border-brand/40 hover:shadow-[0_12px_32px_rgba(10,37,64,0.08)] hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300"
                        >
                            <div className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center text-brand group-hover:bg-brand group-hover:text-white transition-colors">
                                <s.icon className="w-5 h-5" />
                            </div>
                            <h3 className="mt-5 font-display text-lg font-semibold text-navy leading-snug">{s.title}</h3>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
